import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components/macro';
import { Link } from 'react-router-dom';
import { getBrandingColor, colorTypes } from 'src/branding';

/**
 * A styled button.
 * Name and/or icons can be passed in as children.
 * Atributes is passed as to a normal html-button.
 *
 * An action can either be:
 *    *An action to be performed with onclick
 *    *A link with href
 *    *A router link with to
 */

export const StyledButton = styled.button`
  border: 2px solid transparent;
  position: relative;
  padding: ${({ small }) => (small ? '8px 12px' : '12px 20px')};
  border-radius: ${({ small }) => (small ? '3px' : '10px')};
  cursor: pointer;
  transition: background-color 0.1s ease-in, border 0.1s ease-in,
    box-shadow 0.1s ease-in, color 0.1s ease-in;
  white-space: nowrap;
  border: 2px solid ${getBrandingColor(colorTypes.fontLight)};
  line-height: 1.15;
  margin: ${({ margin }) => (margin ? margin : 'initial')};
  font-size: 0.9em;
  text-decoration: none;

  ${({ grow }) => grow
      && css`
        flex-grow: 1;
      `}
    :focus {
    outline: none;
  }

  :hover {
    cursor: pointer;
  }

  &:disabled,
  &:disabled:hover {
    cursor: not-allowed;
  }
  ${({fullwidth}) => fullwidth && css`width: 100%;`}
`;

const PrimaryBtn = styled(StyledButton)`
  background-color: ${getBrandingColor(colorTypes.primaryBtn)};
  color: ${getBrandingColor(colorTypes.primaryBtnFont)};
  font-weight: 600;
  border-radius: 7px;
  border: 2px solid transparent;
  :focus {
    background-color: ${getBrandingColor(colorTypes.primaryBtnFocus)};
  }
  :active,
  :active:focus {
    background-color: ${getBrandingColor(colorTypes.primaryBtnActive)};
  }

  :hover {
    background-color: ${getBrandingColor(colorTypes.primaryBtnHover)};
  }

  &:disabled,
  &:disabled:hover {
    background-color: #f9f9f9;
    color: inherit;
    border: 2px solid #aca6a6;
  }
`;

export const PrimaryCenteredBtn = styled(PrimaryBtn)`
  width: fit-content;
  align-self: center;
  margin-bottom: 0.7rem;
  position: static;
`;

const SecondaryBtn = styled(StyledButton)`
  background-color: ${getBrandingColor(colorTypes.secondaryBtn)};
  color: ${getBrandingColor(colorTypes.secondaryBtnFont)};
  font-weight: 600;
  box-shadow: inset 0px 0px 0px 2px ${getBrandingColor(colorTypes.secondaryBtnBorder)};
  :active,
  :active:focus {
    box-shadow: inset 0px 0px 0px 2px ${getBrandingColor(colorTypes.secondaryBtnHoverBorder)};
  }

  :hover {
    box-shadow: inset 0px 0px 0px 2px ${getBrandingColor(colorTypes.secondaryBtnHoverBorder)};
  }

  &:disabled,
  &:disabled:hover {
    color: gray;
  }
`;

const WarnButton = styled(StyledButton)`
  color: white;
  background-color: ${getBrandingColor(colorTypes.error)};
  font-weight: bold;
`;

const NormalBtn = styled(StyledButton)`
  background-color: ${ props => props.backgroundColor || getBrandingColor(colorTypes.normalBtn)};
  :focus {
    background-color: ${getBrandingColor(colorTypes.normalBtnFocus)};
  }
  :active,
  :active:focus {
    background-color: ${getBrandingColor(colorTypes.normalBtnActive)};
  }

  :hover {
    background-color: ${getBrandingColor(colorTypes.normalBtnHover)};
  }

  &:disabled,
  &:disabled:hover {
    background-color: ${getBrandingColor(colorTypes.normalBtnDisabled)};
  }
`;

const Button = ({
  secondary,
  primary,
  primaryCentered,
  warn,
  className,
  alternative,
  onClick,
  href,
  to,
  children,
  margin,
  ...props
}) => {
  if (href) {
    Object.assign(props, {
      as: 'a',
      href,
    });
  }

  const wrapAsRouterLink = (elemToWrap) => (to ? <Link to={to}>{elemToWrap}</Link> : elemToWrap);

  let ChoosedBtn = null;
  if (primary) {
    ChoosedBtn = PrimaryBtn;
  } else if (primaryCentered) {
    ChoosedBtn = PrimaryCenteredBtn;
  } else if (secondary) {
    ChoosedBtn = SecondaryBtn;
  } else if (warn) {
    ChoosedBtn = WarnButton;
  } else {
    ChoosedBtn = NormalBtn;
  }

  return wrapAsRouterLink(
    <ChoosedBtn
      {...(onClick ? { onClick } : {})}
      role="button"
      {...props}
      margin={margin}
    >
      {children}
    </ChoosedBtn>,
  );
};

export default Button;

Button.propTypes = {
  small: PropTypes.bool,
  onClick: PropTypes.func,
  href: PropTypes.string,
  to: PropTypes.string,
  children: PropTypes.node,
};

Button.defaultProps = {
  small: false,
  onClick: null,
  to: null,
  href: null,
  children: null,
};
